/**
 * PKCE (RFC 7636) helpers for the native SSO hand-off (#80).
 *
 * The app generates a high-entropy verifier, sends only its S256 challenge when it
 * opens the district SSO flow in the system browser, and keeps the verifier to
 * redeem the one-time code at /api/sso/exchange. The challenge is public (it rides
 * the browser URL); the verifier never leaves the app, so a stolen exchange code is
 * useless to anyone who didn't start the flow.
 *
 * S256 must match the server's SsoTokenExchangePolicy.VerifyPkce:
 * challenge = BASE64URL( SHA256( ASCII(verifier) ) ), no padding.
 */

export interface PkcePair {
  verifier: string;
  challenge: string;
}

/** 43-char base64url verifier (32 random bytes) + its S256 challenge. */
export async function createPkcePair(): Promise<PkcePair> {
  const verifier = randomBase64Url(32);
  const challenge = await sha256Base64Url(verifier);
  return { verifier, challenge };
}

function randomBase64Url(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64Url(bytes);
}

async function sha256Base64Url(value: string): Promise<string> {
  const data = new TextEncoder().encode(value);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64Url(new Uint8Array(digest));
}

function base64Url(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}
